// Phase 16 · Scheduled reports UI + bundle export button
import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, CalendarClock, Package, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const REPORT_KEYS = [
  "attendance","beat-coverage","missed-visits","daily-visit-summary",
  "daily-sales","sku-wise-sales","distributor-trend","activity-submissions",
  "display-compliance","payments","claims","sale-returns","expenses","target-achievement",
];

export default function SchedulesPage() {
  const [schedules, setSchedules] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    report_key: "daily-sales", name: "", cadence: "daily",
    day_of_week: "mon", hour_ist: 8, format: "xlsx", email_to: "",
    filters: {}, is_active: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [showBundle, setShowBundle] = useState(false);
  const [bundleKeys, setBundleKeys] = useState(["daily-sales", "attendance"]);
  const [bundleJob, setBundleJob] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/report-schedules");
      setSchedules(r.data.items || []);
    } catch (e) {
      toast.error("Failed to load schedules");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/report-schedules", form);
      toast.success("Schedule created");
      setShowForm(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSubmitting(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this schedule?")) return;
    try { await api.delete(`/report-schedules/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error("Delete failed"); }
  };

  const runBundle = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
      const r = await api.post("/reports/bundle-export", {
        items: bundleKeys.map(k => ({
          report_key: k,
          params: { date_from: monthAgo, date_to: today, format: "xlsx" },
        })),
      });
      setBundleJob({ ...r.data, status: "processing" });
      // Poll
      for (let i = 0; i < 12; i++) {
        await new Promise(res => setTimeout(res, 1500));
        const j = await api.get(`/reports/bundle-export/${r.data.job_id}`);
        if (["completed", "completed_with_errors", "failed"].includes(j.data.status)) {
          setBundleJob(j.data); break;
        }
      }
    } catch (e) { toast.error("Bundle export failed"); }
  };
  const downloadBundle = () => {
    if (!bundleJob?.id) return;
    const token = localStorage.getItem("token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/v1/reports/bundle-export/${bundleJob.id}/download`;
    window.open(url + `?_t=${Date.now()}`, "_blank");
    // The auth is required — use fetch
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(b => {
        const u = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = u; a.download = `tulsi_bundle_${bundleJob.id.slice(0,8)}.zip`; a.click();
        URL.revokeObjectURL(u);
      });
  };

  if (loading) return <div className="flex justify-center py-20 text-neutral-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="schedules-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CalendarClock className="w-6 h-6 text-[#E5197F]" /> Scheduled reports
          </h1>
          <p className="text-xs text-neutral-500 mt-1">Automatically email selected reports on a cadence.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowBundle(true)} data-testid="bundle-open-btn"
                   className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border text-xs">
            <Package className="w-4 h-4" /> Bundle export
          </button>
          <button onClick={() => setShowForm(true)} data-testid="schedule-new-btn"
                   className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-[#E5197F] text-white text-xs">
            <Plus className="w-4 h-4" /> New schedule
          </button>
        </div>
      </div>

      {schedules.length === 0 ? (
        <div className="text-center py-12 text-sm text-neutral-500 border rounded-xl bg-white"
             data-testid="schedules-empty">No schedules yet.</div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-xs text-neutral-600">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left">Report</th>
                <th>Cadence</th>
                <th>Next run</th>
                <th>Format</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {schedules.map(s => (
                <tr key={s.id} className="border-t" data-testid={`schedule-row-${s.id}`}>
                  <td className="px-4 py-2">{s.name}</td>
                  <td>{s.report_key}</td>
                  <td className="text-center">{s.cadence}{s.day_of_week ? ` (${s.day_of_week})` : ""} @ {s.hour_ist}:00 IST</td>
                  <td className="text-center text-xs">{(s.next_send_at || "").slice(0,16).replace("T"," ")}</td>
                  <td className="text-center">{s.format}</td>
                  <td className="text-right pr-3">
                    <button onClick={() => remove(s.id)} data-testid={`schedule-del-${s.id}`}
                             className="text-rose-600 hover:bg-rose-50 rounded p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg p-5 space-y-3" data-testid="schedule-form">
            <div className="flex justify-between items-center">
              <h2 className="font-semibold">New scheduled report</h2>
              <button onClick={() => setShowForm(false)} className="text-neutral-500 text-sm">Close</button>
            </div>

            <label className="block text-xs">Report
              <select value={form.report_key} onChange={e => setForm({...form, report_key: e.target.value})}
                       data-testid="sch-report-select"
                       className="w-full h-10 px-2 rounded border mt-1">
                {REPORT_KEYS.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </label>
            <label className="block text-xs">Name
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                      placeholder="e.g. Weekly zone sales"
                      data-testid="sch-name-input"
                      className="w-full h-10 px-2 rounded border mt-1" />
            </label>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <label>Cadence
                <select value={form.cadence} onChange={e => setForm({...form, cadence: e.target.value})}
                         data-testid="sch-cadence-select"
                         className="w-full h-9 px-1 rounded border mt-1">
                  <option>daily</option><option>weekly</option><option>monthly</option>
                </select>
              </label>
              <label>Day
                <select value={form.day_of_week} onChange={e => setForm({...form, day_of_week: e.target.value})}
                         disabled={form.cadence !== "weekly"}
                         className="w-full h-9 px-1 rounded border mt-1 disabled:opacity-40">
                  {["mon","tue","wed","thu","fri","sat","sun"].map(d => <option key={d}>{d}</option>)}
                </select>
              </label>
              <label>Hour (IST)
                <input type="number" min="0" max="23"
                        value={form.hour_ist} onChange={e => setForm({...form, hour_ist: Number(e.target.value)})}
                        className="w-full h-9 px-2 rounded border mt-1" />
              </label>
            </div>
            <label className="block text-xs">Email to (blank = your account)
              <input value={form.email_to} onChange={e => setForm({...form, email_to: e.target.value})}
                      type="email" data-testid="sch-emailto-input"
                      className="w-full h-10 px-2 rounded border mt-1" />
            </label>
            <button onClick={submit} disabled={submitting}
                     data-testid="sch-submit-btn"
                     className="w-full h-10 rounded-lg bg-[#E5197F] text-white font-medium text-sm disabled:opacity-50">
              {submitting ? <Loader2 className="w-4 h-4 mx-auto animate-spin" /> : "Create schedule"}
            </button>
          </div>
        </div>
      )}

      {showBundle && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg p-5 space-y-3" data-testid="bundle-dialog">
            <div className="flex justify-between items-center">
              <h2 className="font-semibold flex items-center gap-2"><Package className="w-5 h-5" /> Bundle export</h2>
              <button onClick={() => { setShowBundle(false); setBundleJob(null); }} className="text-neutral-500 text-sm">Close</button>
            </div>
            <p className="text-xs text-neutral-500">Pick up to 10 reports; last 30 days will be exported as xlsx and packaged into a zip.</p>
            <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto">
              {REPORT_KEYS.map(k => (
                <label key={k} className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={bundleKeys.includes(k)}
                          onChange={e => {
                            if (e.target.checked) setBundleKeys([...bundleKeys, k]);
                            else setBundleKeys(bundleKeys.filter(x => x !== k));
                          }}
                          data-testid={`bundle-cb-${k}`}
                          className="accent-[#E5197F]" />
                  {k}
                </label>
              ))}
            </div>
            <button onClick={runBundle} data-testid="bundle-run-btn"
                     className="w-full h-10 rounded-lg bg-[#E5197F] text-white font-medium text-sm">
              Generate bundle
            </button>
            {bundleJob && (
              <div className="text-xs p-2 rounded bg-neutral-50 border" data-testid="bundle-status">
                <div>Job: {bundleJob.job_id || bundleJob.id}</div>
                <div>Status: <b>{bundleJob.status}</b></div>
                {bundleJob.size_bytes && <div>Size: {Math.round(bundleJob.size_bytes / 1024)} KB</div>}
                {bundleJob.errors?.length > 0 && (
                  <div className="text-rose-600">Errors: {bundleJob.errors.length}</div>
                )}
                {(bundleJob.status === "completed" || bundleJob.status === "completed_with_errors") && (
                  <button onClick={downloadBundle} data-testid="bundle-download-btn"
                           className="mt-2 inline-flex items-center gap-1 h-8 px-3 rounded bg-emerald-600 text-white">
                    <Download className="w-4 h-4" /> Download zip
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
